import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Category, CategoryFolder } from "~/api/model";
import {
  getListOrganizationCategoriesQueryKey,
  getListOrganizationCategoryFoldersQueryKey,
  useDeleteOrganizationCategoryFolder,
} from "~/api/organization/organization";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";

interface DeleteFolderAlertDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  folderId: number;
  hasCategories: boolean;
}

export const DeleteFolderAlertDialog: FC<DeleteFolderAlertDialogProps> = ({
  open,
  setOpen,
  folderId,
  hasCategories,
}) => {
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: deleteFolder } = useDeleteOrganizationCategoryFolder();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const intl = useIntl();

  const handleDeleteFolder = () => {
    const foldersQueryKey =
      getListOrganizationCategoryFoldersQueryKey(organizationId);
    const categoriesQueryKey =
      getListOrganizationCategoriesQueryKey(organizationId);

    // Save previous data for rollback
    const previousFolders =
      queryClient.getQueryData<CategoryFolder[]>(foldersQueryKey);
    const previousCategories =
      queryClient.getQueryData<Category[]>(categoriesQueryKey);

    // Optimistic update: immediately remove folder from cache
    queryClient.setQueryData(
      foldersQueryKey,
      (oldData: CategoryFolder[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.filter((f) => f.id !== folderId);
      }
    );

    // Optimistic update: set folderId to null for categories in this folder
    queryClient.setQueryData(
      categoriesQueryKey,
      (oldData: Category[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map((c) =>
          c.folderId === folderId ? { ...c, folderId: null } : c
        );
      }
    );

    // Close dialog immediately
    setOpen(false);

    // Delete in background with error rollback
    deleteFolder({
      organizationId,
      folderId,
    }).catch(() => {
      queryClient.setQueryData(foldersQueryKey, previousFolders);
      queryClient.setQueryData(categoriesQueryKey, previousCategories);
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "folder.deleteError" }),
      });
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage id="folder.delete.title" />
          </AlertDialogTitle>
          <AlertDialogDescription>
            {hasCategories ? (
              <FormattedMessage id="folder.delete.descriptionWithCategories" />
            ) : (
              <FormattedMessage id="folder.delete.description" />
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button variant="destructive" onClick={handleDeleteFolder}>
            <FormattedMessage id="delete" defaultMessage="Delete" />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
